import React, { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Share, PlusSquare, CheckCircle2, X } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { recordPwaDismissal } from "@/hooks/usePwaInstall";

interface PwaInstallModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const PwaInstallModal: React.FC<PwaInstallModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const shouldReduceMotion = useReducedMotion();
    const modalRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const handleClose = useCallback(() => {
        recordPwaDismissal();
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const timer = setTimeout(() => {
            closeButtonRef.current?.focus();
        }, 50);

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                handleClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen, handleClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={shouldReduceMotion ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={shouldReduceMotion ? undefined : { opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={handleClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-xs"
                        aria-hidden="true"
                    />

                    {/* Dialog Card */}
                    <motion.div
                        ref={modalRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pwa-install-title"
                        initial={shouldReduceMotion ? false : { y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={shouldReduceMotion ? undefined : { y: "100%", opacity: 0 }}
                        transition={{ type: "spring", damping: 26, stiffness: 280 }}
                        className="relative z-10 w-full sm:max-w-md bg-white dark:bg-warm-dark-surface rounded-t-3xl sm:rounded-3xl border border-warm-border dark:border-warm-dark-border shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] sm:pb-6 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-800">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10 text-primary dark:text-green-400">
                                    <PlusSquare size={22} aria-hidden="true" />
                                </div>
                                <h2
                                    id="pwa-install-title"
                                    className="text-base font-bold text-secondary dark:text-gray-100"
                                >
                                    {t("pwa.installTitle")}
                                </h2>
                            </div>
                            <button
                                ref={closeButtonRef}
                                type="button"
                                onClick={handleClose}
                                className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] min-w-[44px] flex items-center justify-center"
                                aria-label={t("nav.closeMenu")}
                            >
                                <X size={20} aria-hidden="true" />
                            </button>
                        </div>

                        {/* Steps */}
                        <div className="py-4 space-y-4">
                            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                {t("pwa.installSubtitle")}
                            </p>

                            <ol className="space-y-3">
                                <li className="flex items-start gap-3 p-3 rounded-2xl bg-gray-50/80 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-750">
                                    <div className="p-2 rounded-lg bg-white dark:bg-gray-700 text-primary dark:text-green-400 shadow-xs shrink-0">
                                        <Share size={18} aria-hidden="true" />
                                    </div>
                                    <div className="text-xs">
                                        <span className="font-bold text-secondary dark:text-gray-100 block">
                                            {t("pwa.step1Title")}
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-400">
                                            {t("pwa.step1Desc")}
                                        </span>
                                    </div>
                                </li>

                                <li className="flex items-start gap-3 p-3 rounded-2xl bg-gray-50/80 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-750">
                                    <div className="p-2 rounded-lg bg-white dark:bg-gray-700 text-primary dark:text-green-400 shadow-xs shrink-0">
                                        <PlusSquare size={18} aria-hidden="true" />
                                    </div>
                                    <div className="text-xs">
                                        <span className="font-bold text-secondary dark:text-gray-100 block">
                                            {t("pwa.step2Title")}
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-400">
                                            {t("pwa.step2Desc")}
                                        </span>
                                    </div>
                                </li>

                                <li className="flex items-start gap-3 p-3 rounded-2xl bg-gray-50/80 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-750">
                                    <div className="p-2 rounded-lg bg-white dark:bg-gray-700 text-primary dark:text-green-400 shadow-xs shrink-0">
                                        <CheckCircle2 size={18} aria-hidden="true" />
                                    </div>
                                    <div className="text-xs">
                                        <span className="font-bold text-secondary dark:text-gray-100 block">
                                            {t("pwa.step3Title")}
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-400">
                                            {t("pwa.step3Desc")}
                                        </span>
                                    </div>
                                </li>
                            </ol>
                        </div>

                        {/* Dismiss Button */}
                        <div className="pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="w-full min-h-[48px] py-2.5 px-4 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-all shadow-sm"
                            >
                                {t("pwa.gotIt")}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default PwaInstallModal;
